import { useEffect, useState } from "react";
import { Copy, Download, Eye, Globe, Loader2, Mail, MapPin, Megaphone, MessageCircle, Pause, Pencil, Phone, Play, Radar, Search, Send, ShieldCheck, Sparkles, Square, Trash2, UserCheck, X, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "../../lib/api";
import { fmtDateMtl } from "../../lib/dateMtl";
import { plur } from "../../lib/localize";
import ExpandableText from "./ExpandableText";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";

const SPORTS = ["Hockey", "Soccer", "Baseball", "Football", "Golf", "Basketball",
  "Volleyball", "Ringuette", "Natation", "Scolaire", "Entreprise", "Autre"];

const STATUS_LABELS = {
  nouveau: "Nouveau",
  contacte: "Contacté",
  client: "Client",
  inactif: "Inactif",
};

// Verdicts de délivrabilité (bouton « Valider les adresses », 2026-10-08)
const VERIF_LABELS = {
  valide: "Adresse valide (confirmée par le serveur du destinataire)",
  mx_seulement: "Domaine valide — boîte non sondable",
  incertain: "Validation incertaine (greylisting du serveur)",
  sans_mx: "Domaine sans serveur courriel — rebond certain",
  boite_inconnue: "Boîte inexistante — rebond certain",
  syntaxe_invalide: "Adresse mal formée",
};
const VERIF_COLORS = {
  valide: "bg-emerald-400", mx_seulement: "bg-amber-400", incertain: "bg-amber-400",
  sans_mx: "bg-red-400", boite_inconnue: "bg-red-400", syntaxe_invalide: "bg-red-400",
};

// Capteur d'adresses courriel : analyse le site d'un organisme sportif,
// en extrait les courriels publics et les classe par sport pour les campagnes.
export default function ProspectsScraper() {
  const [url, setUrl] = useState("");
  const [sport, setSport] = useState("auto");
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  // Capture en masse : liste de sites (un par ligne), traitée en tâche de fond
  const [mode, setMode] = useState("single"); // "single" | "bulk" | "city"
  const [bulkUrls, setBulkUrls] = useState("");
  const [bulkJob, setBulkJob] = useState(null);
  // Recherche par ville (Google Places) : organismes d'un sport dans une ville
  const [city, setCity] = useState("");
  const [citySport, setCitySport] = useState("Hockey");
  const [cityBusy, setCityBusy] = useState(false);
  const [cityResults, setCityResults] = useState(null);
  const [citySelected, setCitySelected] = useState({});
  // Pagination serveur (25/50/100 par page) — la page reste légère avec des
  // milliers de prospects
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [pages, setPages] = useState(1);
  const [filtered, setFiltered] = useState(0);
  // Compteurs des cases cochées (avec / sans site) — les boutons d'action sont
  // désactivés à zéro pour qu'un clic « sans effet » soit impossible à mal lire
  const nSitesCoches = (cityResults?.results || []).filter((r, i) => citySelected[i] && r.website).length;
  const nSansSiteCoches = (cityResults?.results || []).filter((r, i) => citySelected[i] && !r.website).length;
  const [saveSport, setSaveSport] = useState("Hockey"); // catégorie d'enregistrement des prospects captés
  const [markSent, setMarkSent] = useState(false);      // marquer « contacté » à la capture
  const [sentAt, setSentAt] = useState(() => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16));
  const [relanceLe, setRelanceLe] = useState("");       // relance prévue (optionnel)
  const [prospects, setProspects] = useState([]);
  const [bySport, setBySport] = useState({});
  const [total, setTotal] = useState(0);
  const [aRelancer, setARelancer] = useState(0);
  const [filterSport, setFilterSport] = useState("tous");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  // Tri des colonnes (clic d'en-tête : A→Z, reclic : Z→A) — côté serveur, toutes pages
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState(-1);

  // En-tête de colonne triable : clic = A→Z, reclic = inverse, indicateur visuel
  const SortHead = ({ field, children, className = "", testid }) => (
    <TableHead
      className={`${className} cursor-pointer select-none hover:text-gold transition-colors`}
      data-testid={testid}
      title="Cliquer pour classer en ordre alphabétique — recliquer pour inverser"
      onClick={() => {
        setPage(1);
        if (sortField === field) setSortDir((d) => -d);
        else { setSortField(field); setSortDir(1); }
      }}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        {sortField === field
          ? (sortDir === 1 ? <ChevronUp size={13} className="text-gold" /> : <ChevronDown size={13} className="text-gold" />)
          : <ChevronsUpDown size={13} className="opacity-30" />}
      </span>
    </TableHead>
  );
  // Générateur de campagne B2B (brouillon par sport — l'envoi se fait depuis le
  // courriel du propriétaire ; les listes captées ne partent pas du service du site)
  const [campOpen, setCampOpen] = useState(false);
  const [campSport, setCampSport] = useState("Hockey");
  const [draft, setDraft] = useState(null);
  const [draftBusy, setDraftBusy] = useState(false);
  const [campSubject, setCampSubject] = useState("");
  const [campBody, setCampBody] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  // Écriture à UN prospect : aperçu (œil) ou modification (crayon) avant l'envoi
  const [writeProspect, setWriteProspect] = useState(null);
  const [writeMode, setWriteMode] = useState("edit"); // "edit" | "preview"
  const [writeSubject, setWriteSubject] = useState("");
  const [writeBody, setWriteBody] = useState("");
  const [writeBusy, setWriteBusy] = useState(false);

  // Sélection par cases (export Excel / suppression en lot) + fiche complète (voir / modifier)
  // selectKind : null (cases individuelles) | "all" (Tout cocher) | "emails" | "phones" —
  // les boutons « Tous les courriels » / « Tous les téléphones » COCHENT toutes les fiches
  // ayant un courriel / un téléphone dans le résultat affiché, en vue d'un futur envoi ;
  // « Excel de la sélection » produit alors la liste des éléments cochés.
  const [selectedIds, setSelectedIds] = useState({});
  const [selectKind, setSelectKind] = useState(null);
  const [withEmail, setWithEmail] = useState(0);   // fiches du résultat ayant un courriel
  const [withPhone, setWithPhone] = useState(0);   // … ayant un téléphone
  const [exportBusy, setExportBusy] = useState(false);
  const [viewProspect, setViewProspect] = useState(null);
  const [editProspect, setEditProspect] = useState(null);  // copie de travail de la fiche
  const [editBusy, setEditBusy] = useState(false);
  const nSelected = Object.values(selectedIds).filter(Boolean).length;

  // Compte no-reply@trophee.ca (règle permanente 2026-10-08) : la prospection part
  // de cette boîte ; le robot retire automatiquement les adresses en rebond et
  // réachemine les réponses à info@trophee.ca. Carte de suivi + relevé manuel.
  const [nrRebonds, setNrRebonds] = useState(null); // null = pas encore chargé
  const [nrBusy, setNrBusy] = useState(false);
  const loadRebonds = async () => {
    try {
      const { data } = await api.get("/admin/noreply/rebonds");
      setNrRebonds(data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { loadRebonds(); }, []);
  const releverNoreply = async () => {
    setNrBusy(true);
    try {
      const { data } = await api.post("/admin/noreply/relever");
      if (data.skipped) toast.error("Boîte no-reply non configurée — voir la note dans la carte ci-dessous");
      else if (data.error) toast.error(`Relevé échoué : ${data.error}`);
      else toast.success(`Relevé terminé — ${data.lus || 0} message(s), ${data.rebonds || 0} adresse(s) retirée(s), ${data.reachemines || 0} réponse(s) acheminée(s) à info@trophee.ca`);
      loadRebonds();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setNrBusy(false);
    }
  };

  // Aperçu du courriel de campagne (rendu EXACT : bilingue + signature) et
  // validation de délivrabilité des adresses (demandes du propriétaire, 2026-10-08)
  const [apercu, setApercu] = useState(null);
  const [apercuBusy, setApercuBusy] = useState(false);
  const [verifJob, setVerifJob] = useState(null);
  const [verifBusy, setVerifBusy] = useState(false);

  const showApercu = async () => {
    setApercuBusy(true);
    try {
      const { data } = await api.post("/admin/prospects/apercu-campagne",
        { subject: campSubject, body: campBody });
      setApercu(data.html);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setApercuBusy(false);
    }
  };

  const validerAdresses = async () => {
    setVerifBusy(true);
    try {
      const ids = nSelected > 0 ? Object.keys(selectedIds).filter((k2) => selectedIds[k2]) : [];
      const { data } = await api.post("/admin/prospects/verifier-adresses", { ids });
      toast.success(`Validation lancée pour ${data.total} adresse(s) — syntaxe, serveur courriel et boîte sondée`);
      const t0 = Date.now();
      const poll = async () => {
        try {
          const r = await api.get(`/admin/prospects/verif-jobs/${data.job_id}`);
          setVerifJob(r.data);
          if (!r.data.done && Date.now() - t0 < 10 * 60 * 1000) {
            setTimeout(poll, 3000);
          } else if (r.data.done) {
            const s = r.data.stats || {};
            const mauvaises = (s.boite_inconnue || 0) + (s.sans_mx || 0) + (s.syntaxe_invalide || 0);
            toast.success(`Validation terminée : ${s.valide || 0} valide(s), ${mauvaises} problématique(s) (exclues des campagnes)`);
            load();
          }
        } catch { /* silencieux */ }
      };
      setTimeout(poll, 3000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setVerifBusy(false);
    }
  };

  // Une case est cochée si : cochée individuellement, ou couverte par le mode global actif
  const rowChecked = (p) =>
    selectKind === "all" || (selectKind === "emails" && !!p.email) ||
    (selectKind === "phones" && !!p.phone) || !!selectedIds[p.id];

  // Décocher une fiche pendant un mode global : bascule en sélection explicite
  // (la page courante moins cette fiche) — les autres pages ne sont plus cochées.
  const toggleSelect = (id, on) => {
    if (selectKind && !on) {
      const next = {};
      prospects.forEach((p) => { next[p.id] = p.id !== id && rowChecked(p); });
      setSelectKind(null);
      setSelectedIds(next);
      return;
    }
    setSelectedIds((s) => ({ ...s, [id]: on }));
  };
  const toggleSelectPage = (on) => {
    setSelectKind(null);
    setSelectedIds((s) => {
      const next = { ...s };
      prospects.forEach((p) => { next[p.id] = on; });
      return next;
    });
  };
  const clearSelection = () => { setSelectKind(null); setSelectedIds({}); };

  // Boutons « Tout cocher » / « Tous les courriels » / « Tous les téléphones » :
  // sélectionnent leur groupe (recliquer décoche) — l'export se fait ensuite avec
  // « Excel de la sélection ».
  const toggleKind = (kind) => {
    if (selectKind === kind) { clearSelection(); return; }
    setSelectKind(kind);
    setSelectedIds({});
  };

  // Filtres actuellement appliqués (modes globaux : export/suppression côté serveur)
  const activeFilters = (kind) => ({
    all: true,
    ...(filterSport === "relance" ? { relance: 1 } : filterSport !== "tous" ? { sport: filterSport } : {}),
    q: search.trim(),
    ...(kind ? { kind } : {}),
  });

  // Téléchargement d'un CSV généré côté serveur (blob → fichier navigateur)
  const downloadCsv = async (payload, filename) => {
    const res = await api.post("/admin/prospects/export-selected.csv", payload, { responseType: "blob" });
    const urlObj = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = urlObj;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(urlObj);
  };

  // « Excel de la sélection » : la liste des éléments COCHÉS — cases individuelles ou
  // « Tout cocher » = tableau complet ; modes courriels/téléphones = liste épurée
  // d'une colonne (une entrée par ligne, dédoublonnée), prête pour un envoi.
  const exportSelection = async () => {
    setExportBusy(true);
    try {
      if (selectKind) {
        const kind = selectKind === "all" ? undefined : selectKind;
        await downloadCsv(activeFilters(kind),
          selectKind === "all" ? "prospects-selection.csv" : `prospects-${selectKind}.csv`);
        toast.success(selectKind === "all"
          ? "Liste Excel enregistrée (prospects-selection.csv) — s'ouvre dans Excel"
          : `Liste enregistrée (prospects-${selectKind}.csv) — toutes les infos de chaque fiche cochée, prête pour votre envoi`);
      } else {
        await downloadCsv({ ids: Object.entries(selectedIds).filter(([, v]) => v).map(([k]) => k) },
          "prospects-selection.csv");
        toast.success("Liste Excel enregistrée (prospects-selection.csv) — s'ouvre dans Excel");
      }
    } catch {
      toast.error("Export Excel impossible");
    } finally {
      setExportBusy(false);
    }
  };

  const removeSelected = async () => {
    if (selectKind) {
      const label = selectKind === "all"
        ? `TOUTES les ${filtered} fiches du résultat affiché (toutes pages)`
        : selectKind === "emails"
          ? `les ${withEmail} fiches AVEC courriel du résultat affiché`
          : `les ${withPhone} fiches AVEC téléphone du résultat affiché`;
      if (!window.confirm(`Supprimer définitivement ${label} ?`)) return;
      try {
        const { data } = await api.post("/admin/prospects/delete-bulk",
          activeFilters(selectKind === "all" ? undefined : selectKind));
        toast.success(`${plur(data.deleted, "prospect")} ${data.deleted > 1 ? "supprimés" : "supprimé"}`);
        clearSelection();
        setPage(1);
        load(1);
      } catch {
        toast.error("Suppression en lot impossible");
      }
      return;
    }
    const ids = Object.entries(selectedIds).filter(([, v]) => v).map(([k]) => k);
    if (!ids.length) return;
    if (!window.confirm(`Supprimer définitivement ${plur(ids.length, "prospect")} ${ids.length > 1 ? "cochés" : "coché"} ?`)) return;
    try {
      const { data } = await api.post("/admin/prospects/delete-bulk", { ids });
      toast.success(`${plur(data.deleted, "prospect")} ${data.deleted > 1 ? "supprimés" : "supprimé"}`);
      clearSelection();
      setPage(1);
      load(1);
    } catch {
      toast.error("Suppression en lot impossible");
    }
  };

  // Enregistre la fiche complète modifiée — pose le verrou « modifié manuellement »
  // côté serveur : plus aucune capture/recherche ne pourra écraser cette fiche.
  const saveEdit = async () => {
    if (!editProspect) return;
    setEditBusy(true);
    try {
      await api.patch(`/admin/prospects/${editProspect.id}`, {
        org: editProspect.org, email: editProspect.email, phone: editProspect.phone,
        address: editProspect.address, city: editProspect.city,
        source_url: editProspect.source_url, sport: editProspect.sport,
        status: editProspect.status, relance_le: editProspect.relance_le || "",
      });
      toast.success("Fiche enregistrée — protégée contre tout écrasement automatique");
      setEditProspect(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Enregistrement impossible");
    } finally {
      setEditBusy(false);
    }
  };

  const load = async (pg = page) => {
    try {
      const params = { page: pg, limit: pageSize, sort: sortField, dir: sortDir };
      if (filterSport === "relance") params.relance = 1;
      else if (filterSport !== "tous") params.sport = filterSport;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/admin/prospects", { params });
      setProspects(data.prospects);
      setBySport(data.by_sport);
      setTotal(data.total);
      setFiltered(data.filtered ?? data.total);
      setPages(data.pages || 1);
      setARelancer(data.a_relancer || 0);
      setWithEmail(data.with_email || 0);
      setWithPhone(data.with_phone || 0);
    } catch { /* silencieux */ } finally {
      setLoading(false);
    }
  };

  // Tout changement de filtre/taille de page ramène à la page 1 et vide la sélection
  useEffect(() => { setPage(1); clearSelection(); }, [filterSport, pageSize]);
  useEffect(() => { load(); }, [filterSport, page, pageSize, sortField, sortDir]);

  // Suivi de la capture en masse : interrogation toutes les 2 s jusqu'à la fin
  useEffect(() => {
    if (!bulkJob || bulkJob.status === "termine") return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/prospects/scan-bulk/${bulkJob.job_id}`);
        setBulkJob(data);
        if (data.status === "termine") {
          load();
          toast.success("Capture en masse terminée");
        }
      } catch { /* silencieux */ }
    }, 2000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJob?.job_id, bulkJob?.status]);

  const startBulk = async () => {
    if (!bulkUrls.trim()) return toast.error("Collez les adresses des sites (une par ligne)");
    try {
      const { data } = await api.post("/admin/prospects/scan-bulk", {
        urls: bulkUrls,
        sport: sport === "auto" ? "" : sport,
      });
      setBulkJob({ job_id: data.job_id, total: data.total, done: 0, status: "en_cours", results: {} });
      toast.success(`Capture lancée pour ${plur(data.total, "site")} — suivez la progression ci-dessous`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lancement impossible");
    }
  };

  // Recherche Google Maps : organismes du sport choisi dans la ou les villes saisies
  const searchCity = async () => {
    if (!city.trim()) return toast.error("Entrez une ou plusieurs villes (une par ligne) : Châteauguay, Léry…");
    setCityBusy(true);
    setCityResults(null);
    try {
      const { data } = await api.post("/admin/prospects/search-city", { sport: citySport, cities: city });
      setCityResults(data);
      setSaveSport(citySport); // par défaut, on enregistre dans la catégorie recherchée
      const sel = {};
      data.results.forEach((r, i) => { if (r.website) sel[i] = true; });
      setCitySelected(sel);
      if (data.count === 0) {
        toast.info("Aucun organisme trouvé — essayez une ville voisine ou un autre sport");
      } else {
        toast.success(`${plur(data.count, "organisme")} ${data.count > 1 ? "trouvés" : "trouvé"} — ${data.with_site} avec site web`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Recherche impossible");
    } finally {
      setCityBusy(false);
    }
  };

  // Lance la capture des courriels sur les sites cochés (réutilise la capture en masse)
  const captureCity = async () => {
    const chosen = (cityResults?.results || []).filter((r, i) => citySelected[i] && r.website);
    const urls = chosen.map((r) => r.website);
    if (!urls.length) return toast.error("Cochez au moins un organisme avec un site web");
    const url_cities = {};
    chosen.forEach((r) => { url_cities[r.website] = r.city || ""; });
    try {
      const payload = { urls: urls.join("\n"), sport: saveSport, url_cities };
      if (markSent && sentAt) payload.contacted_at = sentAt;
      if (relanceLe) payload.relance_le = relanceLe;
      const { data } = await api.post("/admin/prospects/scan-bulk", payload);
      setBulkJob({ job_id: data.job_id, total: data.total, done: 0, status: "en_cours", results: {} });
      toast.success(`Capture lancée pour ${plur(data.total, "site")} — catégorie « ${saveSport} »`
        + (markSent ? `, marqués envoyés le ${sentAt.replace("T", " à ")}` : "")
        + (relanceLe ? `, relance le ${relanceLe}` : ""));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Lancement impossible");
    }
  };

  // Lien Messenger dérivé d'une page Facebook (m.me/<page>)
  const messengerLink = (url) => {
    const m = (url || "").match(/facebook\.com\/(?:pg\/)?([^\/?\s]+)/i);
    return m ? `https://m.me/${m[1]}` : url;
  };

  // Enregistre les organismes SANS site web cochés (contact téléphone/Messenger)
  const saveOrgs = async () => {
    const orgs = (cityResults?.results || []).filter((r, i) => citySelected[i] && !r.website)
      .map((r) => ({ name: r.name, address: r.address, phone: r.phone, social_url: r.social_url || "", city: r.city || "" }));
    if (!orgs.length) return toast.error("Cochez au moins un organisme sans site web");
    try {
      const payload = { orgs, sport: saveSport };
      if (markSent && sentAt) payload.contacted_at = sentAt;
      if (relanceLe) payload.relance_le = relanceLe;
      const { data } = await api.post("/admin/prospects/save-orgs", payload);
      toast.success(`${plur(data.saved, "contact")} ${data.saved > 1 ? "enregistrés" : "enregistré"} dans « ${saveSport} » — à joindre par téléphone ou Messenger`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Enregistrement impossible");
    }
  };

  // Bloc de progression de la capture en masse (partagé entre mode liste et mode ville)
  const bulkProgressBlock = bulkJob && (
    <div className="bg-ink border border-gold/25 rounded-xl p-4" data-testid="bulk-progress">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-widest text-gold">
          {bulkJob.status === "termine" ? "Capture terminée" : `Capture en cours — ${bulkJob.done}/${bulkJob.total}`}
        </p>
        {bulkJob.status === "termine" && (
          <button onClick={() => { setBulkJob(null); setBulkUrls(""); }} data-testid="bulk-close"
            className="text-ivory/50 hover:text-gold text-xs transition-colors">Fermer</button>
        )}
      </div>
      <div className="h-1.5 bg-ivory/10 rounded-full overflow-hidden mb-3">
        <div className="h-full bg-gold transition-all duration-500"
          style={{ width: `${bulkJob.total ? (bulkJob.done / bulkJob.total) * 100 : 0}%` }} />
      </div>
      <ul className="space-y-1 max-h-44 overflow-y-auto">
        {Object.entries(bulkJob.results || {}).map(([i, r]) => (
          <li key={i} className="text-xs flex items-center justify-between gap-3" data-testid={`bulk-result-${i}`}>
            <ExpandableText text={r.url} testId={`bulk-result-url-${i}`} className="min-w-0 flex-1 text-ivory/70" />
            <span className={r.found > 0 ? "text-emerald-400 shrink-0" : "text-ivory/40 shrink-0"}>
              {r.error ? "injoignable" : r.found > 0 ? `${plur(r.found, "courriel")} · ${r.sport || "—"}` : "aucun"}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );

  // Sauvegarde la version modifiée comme gabarit réutilisable pour ce sport
  const saveTemplate = async () => {
    try {
      await api.post("/admin/prospects/templates", { sport: campSport, subject: campSubject, body: campBody });
      setDraft((d) => ({ ...d, custom: true }));
      toast.success(`Gabarit « ${campSport} » sauvegardé — utilisé automatiquement pour ce sport`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sauvegarde impossible");
    }
  };

  const resetTemplate = async () => {
    try {
      await api.delete(`/admin/prospects/templates/${encodeURIComponent(campSport)}`);
      toast.success("Retour au modèle par défaut pour ce sport");
      setDraftBusy(true);
      const { data } = await api.get("/admin/prospects/campaign-draft", { params: { sport: campSport } });
      setDraft(data);
      setCampSubject(data.subject);
      setCampBody(data.body);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Suppression impossible");
    } finally {
      setDraftBusy(false);
    }
  };

  // Charge le brouillon de campagne à l'ouverture du dialogue / changement de sport
  useEffect(() => {
    if (!campOpen) return;
    setDraftBusy(true);
    api.get("/admin/prospects/campaign-draft", { params: { sport: campSport } })
      .then((r) => {
        setDraft(r.data);
        setCampSubject(r.data.subject);
        setCampBody(r.data.body);
      })
      .catch(() => { setDraft(null); toast.error("Brouillon indisponible"); })
      .finally(() => setDraftBusy(false));
  }, [campOpen, campSport]);

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch {
      toast.error("Copie impossible — sélectionnez le texte manuellement");
    }
  };

  // Marque les prospects du sport comme « Contacté » (date/heure enregistrée) —
  // appelé quand le brouillon est utilisé (copie des adresses ou ouverture courriel)
  const markContacted = async () => {
    try {
      const { data } = await api.post("/admin/prospects/mark-contacted", { sport: campSport });
      if (data.marked > 0) {
        toast.success(`${plur(data.marked, "prospect")} ${data.marked > 1 ? "marqués" : "marqué"} « Contacté » — vous ne leur écrirez pas deux fois`);
        load();
      }
    } catch { /* silencieux : le marquage est un confort, pas un blocage */ }
  };

  // ENVOI DIRECT de la campagne au groupe sélectionné (demande du propriétaire) :
  // max 100 courriels/heure — le surplus part en file automatiquement l'heure suivante.
  const [sendBusy, setSendBusy] = useState(false);
  const sendCampaign = async () => {
    if (!draft || draft.count === 0) return;
    if (!window.confirm(
      `Envoyer le courriel aux ${draft.count} prospects « ${campSport} » maintenant ?\n\n` +
      `Maximum 100 courriels/heure — au-delà, le surplus part automatiquement à l'heure suivante.\n` +
      `Les clients, inactifs et désabonnés sont exclus.`)) return;
    setSendBusy(true);
    try {
      const { data } = await api.post("/admin/prospects/campaign-send", {
        sport: campSport, subject: campSubject, body: campBody,
      });
      toast.success(`Campagne lancée pour ${plur(data.total, "prospect")} — max 100/h, le surplus part en file. Suivez la progression dans le journal ci-dessous.`);
      setCampOpen(false);
      load();
      loadCampJobs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Envoi impossible");
    } finally {
      setSendBusy(false);
    }
  };

  // Journal des envois de campagne (demande du propriétaire) : statut en direct,
  // pause / reprise / arrêt complet — la progression se rafraîchit toutes les 3 s
  // tant qu'un envoi n'est pas terminé.
  const [campJobs, setCampJobs] = useState([]);
  const [campJobBusy, setCampJobBusy] = useState("");
  const loadCampJobs = async () => {
    try {
      const { data } = await api.get("/admin/prospects/campaign-jobs");
      setCampJobs(data);
    } catch { /* silencieux */ }
  };
  useEffect(() => { loadCampJobs(); }, []);
  useEffect(() => {
    if (!campJobs.some((j) => !j.done)) return;
    const t = setInterval(loadCampJobs, 3000);
    return () => clearInterval(t);
  }, [campJobs]);
  const campJobControl = async (jobId, action) => {
    if (action === "stop" && !window.confirm(
      "Arrêter complètement cet envoi ? Les prospects restants ne recevront RIEN. Cette action est définitive pour cet envoi.")) return;
    setCampJobBusy(`${action}-${jobId}`);
    try {
      await api.post(`/admin/prospects/campaign-jobs/${jobId}/control`, { action });
      toast.success(action === "pause" ? "Envoi mis en pause — reprenez quand vous voulez"
        : action === "resume" ? "Envoi repris"
        : "Arrêt demandé — l'envoi s'arrête immédiatement");
      loadCampJobs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action impossible");
    } finally {
      setCampJobBusy("");
    }
  };

  // Rédaction IA : Claude réécrit objet + message pour le sport choisi (modifiable après)
  const aiDraft = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post("/admin/prospects/campaign-ai", {
        sport: campSport,
        count: draft?.count || 0,
      });
      setCampSubject(data.subject);
      setCampBody(data.body);
      toast.success("Message rédigé par l'IA — relisez et ajustez avant l'envoi");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Rédaction IA indisponible");
    } finally {
      setAiBusy(false);
    }
  };

  const scan = async () => {
    if (!url.trim()) return toast.error("Entrez l'adresse du site de l'organisme");
    setScanning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/prospects/scan", {
        url: url.trim(),
        sport: sport === "auto" ? "" : sport,
      });
      setResult(data);
      if (data.found > 0) {
        toast.success(`${plur(data.found, "courriel")} ${data.found > 1 ? "trouvés" : "trouvé"} — sport : ${data.sport} (${plur(data.new, "nouveau")} ${data.new > 1 ? "ajoutés" : "ajouté"})`);
        setUrl("");
        load();
      } else {
        toast.info(data.pages > 0
          ? `Aucun courriel public trouvé (${plur(data.pages, "page")} ${data.pages > 1 ? "analysées" : "analysée"}). Certains sites n'affichent leurs courriels que via un formulaire.`
          : "Site injoignable ou aucune page de contact accessible.");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Analyse impossible");
    } finally {
      setScanning(false);
    }
  };

  const update = async (p, field, value) => {
    try {
      await api.patch(`/admin/prospects/${p.id}`, { [field]: value });
      setProspects((prev) => prev.map((x) => (x.id === p.id ? { ...x, [field]: value } : x)));
      if (field === "sport") load();
    } catch {
      toast.error("Modification impossible");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer ${p.email || p.org} des prospects ?`)) return;
    try {
      await api.delete(`/admin/prospects/${p.id}`);
      setProspects((prev) => prev.filter((x) => x.id !== p.id));
      setTotal((t) => t - 1);
      toast.success("Prospect supprimé");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  // Ouvre le dialogue d'écriture à un prospect précis (aperçu ou modification),
  // prérempli avec le gabarit de son sport
  const openWrite = async (p, mode) => {
    setWriteProspect(p);
    setWriteMode(mode);
    setWriteBusy(true);
    try {
      const { data } = await api.get("/admin/prospects/campaign-draft", { params: { sport: p.sport } });
      setWriteSubject(data.subject);
      setWriteBody(data.body);
    } catch {
      toast.error("Gabarit indisponible");
    } finally {
      setWriteBusy(false);
    }
  };

  // Marque CE prospect comme contacté (date/heure enregistrées côté serveur)
  const markOneContacted = async (p) => {
    if (p.status !== "nouveau") return;
    try {
      await api.patch(`/admin/prospects/${p.id}`, { status: "contacte" });
      load();
      toast.success(`${p.email} marqué « Contacté »`);
    } catch { /* silencieux */ }
  };

  const exportUrl = `${API_BASE}/api/admin/prospects-export.csv${filterSport !== "tous" ? `?sport=${encodeURIComponent(filterSport)}` : ""}`;

  return (
    <div data-testid="prospects-scraper" className="space-y-8">
      <div className="bg-surface border border-ivory/10 rounded-xl p-6">
        <h2 className="font-serif text-2xl text-champagne mb-2 flex items-center gap-3">
          <Radar size={22} className="text-gold" /> Capteur d'adresses courriel
        </h2>
        <p className="text-sm text-ivory/60 mb-6">
          Entrez le site web d'un organisme (association, ligue, club, école) : l'outil visite ses pages publiques
          (accueil, contact, équipe…) et en extrait les courriels affichés, classés par sport pour vos futures campagnes.
        </p>
        <div className="flex flex-wrap gap-2 mb-4">
          <button onClick={() => setMode("single")} data-testid="scan-mode-single"
            className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${mode === "single" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
            Un site
          </button>
          <button onClick={() => setMode("bulk")} data-testid="scan-mode-bulk"
            className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${mode === "bulk" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
            Liste de sites (capture en masse)
          </button>
          <button onClick={() => setMode("city")} data-testid="scan-mode-city"
            className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full border text-xs transition-colors ${mode === "city" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
            <MapPin size={12} /> Par ville (Google Maps)
          </button>
        </div>

        {mode === "city" && (
          <div className="space-y-4">
            <p className="text-xs text-ivory/60">
              Recherchez les associations d'un sport dans une ville (comme sur Google Maps) :
              l'outil trouve les organismes et leur site web, puis capte les courriels des sites que vous cochez.
            </p>
            <div className="flex flex-col md:flex-row gap-3">
              <Select value={citySport} onValueChange={setCitySport}>
                <SelectTrigger data-testid="city-sport-select" className="md:w-56 bg-ink border-ivory/15 rounded-full text-ivory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-ivory/15 text-ivory">
                  {SPORTS.filter((s) => s !== "Autre").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <textarea
                data-testid="city-input"
                rows={2}
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={"Villes, une par ligne, ex. :\nChâteauguay\nLéry"}
                className="flex-1 bg-ink border border-ivory/15 rounded-xl px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none transition-colors"
              />
              <button
                data-testid="city-search-btn"
                onClick={searchCity}
                disabled={cityBusy}
                className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-7 py-3 rounded-full text-sm hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50"
              >
                {cityBusy ? <Loader2 size={16} className="animate-spin" /> : <MapPin size={16} />}
                {cityBusy ? "Recherche…" : "Rechercher"}
              </button>
            </div>

            {cityResults && (
              <div className="bg-ink border border-gold/25 rounded-xl p-4" data-testid="city-results">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <p className="text-xs uppercase tracking-widest text-gold">
                    {plur(cityResults.count, "organisme")} — « {cityResults.query} »
                  </p>
                  <button
                    data-testid="city-toggle-all"
                    onClick={() => {
                      const all = cityResults.results.map((_, i) => i);
                      const allOn = all.every((i) => citySelected[i]);
                      const sel = { ...citySelected };
                      all.forEach((i) => { sel[i] = !allOn; });
                      setCitySelected(sel);
                    }}
                    className="text-xs text-ivory/60 hover:text-gold underline underline-offset-2 transition-colors">
                    {cityResults.results.every((_, i) => citySelected[i]) ? "Tout décocher" : "Tout cocher"}
                  </button>
                </div>
                <ul className="space-y-2 max-h-72 overflow-y-auto mb-4">
                  {cityResults.results.map((r, i) => (
                    <li key={`${r.name}-${i}`} data-testid={`city-result-${i}`}
                      className={`flex items-start gap-3 text-xs rounded-lg border px-3 py-2.5 ${r.website ? "border-ivory/10" : "border-amber-500/25"}`}>
                      <input
                        type="checkbox"
                        data-testid={`city-check-${i}`}
                        checked={!!citySelected[i]}
                        onChange={(e) => setCitySelected((s) => ({ ...s, [i]: e.target.checked }))}
                        className="mt-0.5 accent-[#d4af37]"
                      />
                      <div className="min-w-0 flex-1">
                        <ExpandableText text={r.name} testId={`city-result-name-${i}`} className="block text-ivory font-semibold">
                          {r.name}
                          {r.city && (cityResults.cities || []).length > 1 && (
                            <span className="ml-2 text-[10px] font-normal text-gold/80 border border-gold/30 rounded-full px-2 py-0.5">{r.city}</span>
                          )}
                        </ExpandableText>
                        <ExpandableText text={`${r.address}${r.phone ? ` · ${r.phone}` : ""}`} testId={`city-result-address-${i}`}
                          className="block text-ivory/50" />
                        {r.website ? (
                          <a href={r.website} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 text-gold/90 hover:text-gold truncate transition-colors">
                            <Globe size={11} /> {r.website.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                          </a>
                        ) : (
                          <span className="inline-flex flex-wrap items-center gap-x-3 gap-y-1">
                            {r.phone && (
                              <a href={`tel:${r.phone.replace(/[^0-9+]/g, "")}`}
                                className="inline-flex items-center gap-1 text-amber-300 hover:text-gold transition-colors">
                                <Phone size={11} /> {r.phone}
                              </a>
                            )}
                            {r.social_url && (
                              <a href={messengerLink(r.social_url)} target="_blank" rel="noreferrer"
                                className="inline-flex items-center gap-1 text-amber-300 hover:text-gold transition-colors">
                                <MessageCircle size={11} /> Messenger
                              </a>
                            )}
                            <span className="text-ivory/40">sans site web — contact direct</span>
                          </span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-3 mb-4" data-testid="city-save-options">
                  <span className="inline-flex items-center gap-2 text-xs text-ivory/60">
                    Enregistrer dans la catégorie :
                    <Select value={saveSport} onValueChange={setSaveSport}>
                      <SelectTrigger data-testid="city-save-sport" className="h-8 w-40 bg-surface border-ivory/15 rounded-full text-xs text-ivory">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-surface border-ivory/15 text-ivory">
                        {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </span>
                  <label className="inline-flex items-center gap-2 text-xs text-ivory/60 cursor-pointer">
                    <input type="checkbox" checked={markSent} onChange={(e) => setMarkSent(e.target.checked)}
                      data-testid="city-mark-sent" className="accent-[#d4af37]" />
                    Envoyé le
                    <input type="datetime-local" value={sentAt} onChange={(e) => setSentAt(e.target.value)}
                      disabled={!markSent} data-testid="city-sent-at"
                      className="bg-surface border border-ivory/15 rounded px-1.5 py-1 text-[11px] text-ivory/80 [color-scheme:dark] disabled:opacity-40" />
                  </label>
                  <label className="inline-flex items-center gap-2 text-xs text-ivory/60">
                    Relance le
                    <input type="date" value={relanceLe} onChange={(e) => setRelanceLe(e.target.value)}
                      data-testid="city-relance-le"
                      className="bg-surface border border-ivory/15 rounded px-1.5 py-1 text-[11px] text-ivory/80 [color-scheme:dark]" />
                  </label>
                </div>
                <div className="flex flex-wrap gap-3">
                  <button
                    data-testid="city-capture-btn"
                    onClick={captureCity}
                    disabled={bulkJob?.status === "en_cours" || nSitesCoches === 0}
                    title={nSitesCoches === 0 ? "Cochez au moins un organisme avec un site web ci-dessus" : ""}
                    className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-2.5 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {bulkJob?.status === "en_cours" ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                    Capter les courriels des sites cochés ({nSitesCoches})
                  </button>
                  <button
                    data-testid="city-save-orgs-btn"
                    onClick={saveOrgs}
                    disabled={nSansSiteCoches === 0}
                    title={nSansSiteCoches === 0 ? "Cochez au moins un organisme sans site web ci-dessus" : ""}
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-6 py-2.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Phone size={14} /> Enregistrer les contacts sans site ({nSansSiteCoches})
                  </button>
                </div>
                {nSitesCoches === 0 && nSansSiteCoches === 0 && (
                  <p className="text-xs text-amber-300/90 mt-1" data-testid="city-none-checked">
                    Aucun organisme coché — cochez-en au moins un dans la liste ci-dessus (ou « Tout cocher ») pour lancer la capture.
                  </p>
                )}
              </div>
            )}

            {bulkProgressBlock}
          </div>
        )}

        {mode === "bulk" && (
          <div className="space-y-3">
            <textarea
              data-testid="bulk-urls-input"
              rows={5}
              value={bulkUrls}
              onChange={(e) => setBulkUrls(e.target.value)}
              placeholder={"Une adresse par ligne, ex. :\nhockey-exemple.qc.ca\nsoccer-exemple.com\nhttps://www.association-exemple.ca"}
              className="w-full bg-ink border border-ivory/15 rounded-xl px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none font-mono transition-colors"
            />
            <div className="flex flex-col md:flex-row gap-3">
              <Select value={sport} onValueChange={setSport}>
                <SelectTrigger data-testid="prospect-sport-select" className="md:w-56 bg-ink border-ivory/15 rounded-full text-ivory">
                  <SelectValue placeholder="Sport (auto)" />
                </SelectTrigger>
                <SelectContent className="bg-surface border-ivory/15 text-ivory">
                  <SelectItem value="auto">Détection automatique</SelectItem>
                  {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
              <button
                data-testid="bulk-scan-btn"
                onClick={startBulk}
                disabled={bulkJob?.status === "en_cours"}
                className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-7 py-3 rounded-full text-sm hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50"
              >
                {bulkJob?.status === "en_cours" ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
                {bulkJob?.status === "en_cours" ? `Capture en cours… ${bulkJob.done}/${bulkJob.total}` : "Analyser la liste (max 15)"}
              </button>
            </div>

            {bulkProgressBlock}
          </div>
        )}

        {mode === "single" && (
        <div className="flex flex-col md:flex-row gap-3">
          <input
            data-testid="prospect-url-input"
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !scanning && scan()}
            placeholder="ex. : hockey-exemple.qc.ca"
            className="flex-1 bg-ink border border-ivory/15 rounded-full px-5 py-3 text-sm text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none transition-colors"
          />
          <Select value={sport} onValueChange={setSport}>
            <SelectTrigger data-testid="prospect-sport-select" className="md:w-56 bg-ink border-ivory/15 rounded-full text-ivory">
              <SelectValue placeholder="Sport (auto)" />
            </SelectTrigger>
            <SelectContent className="bg-surface border-ivory/15 text-ivory">
              <SelectItem value="auto">Détection automatique</SelectItem>
              {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            data-testid="prospect-scan-btn"
            onClick={scan}
            disabled={scanning}
            className="inline-flex items-center justify-center gap-2 bg-gold text-ink font-bold px-7 py-3 rounded-full text-sm hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300 disabled:opacity-50"
          >
            {scanning ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}
            {scanning ? "Analyse en cours…" : "Analyser le site"}
          </button>
        </div>
        )}

        {result && result.found > 0 && (
          <div data-testid="prospect-scan-result" className="mt-5 bg-ink border border-gold/25 rounded-xl p-4">
            <p className="text-xs uppercase tracking-widest text-gold mb-2">
              {plur(result.found, "courriel")} {result.found > 1 ? "trouvés" : "trouvé"} · sport : {result.sport} · {plur(result.pages, "page")} {result.pages > 1 ? "visitées" : "visitée"}
            </p>
            <div className="flex flex-wrap gap-2">
              {result.emails.map((e) => (
                <span key={e} className="text-xs bg-surface border border-ivory/15 text-ivory/80 rounded-full px-3 py-1">{e}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2" data-testid="prospect-sport-filters">
        <button
          data-testid="prospect-filter-tous"
          onClick={() => setFilterSport("tous")}
          className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${filterSport === "tous" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}
        >
          Tous ({total})
        </button>
        {SPORTS.filter((s) => (bySport[s] || 0) > 0).map((s) => (
          <span key={s} className="inline-flex items-center gap-0.5">
            <button
              data-testid={`prospect-filter-${s.toLowerCase()}`}
              onClick={() => setFilterSport(s)}
              className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${filterSport === s ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}
            >
              {s} ({bySport[s]})
            </button>
            <a
              href={`${API_BASE}/api/admin/prospects-export.csv?sport=${encodeURIComponent(s)}`}
              data-testid={`prospect-excel-${s.toLowerCase()}`}
              title={`Liste Excel des prospects ${s} (pour campagne)`}
              className="p-1.5 rounded-full border border-emerald-500/30 text-emerald-400/80 hover:bg-emerald-500 hover:text-ink transition-colors"
            >
              <Download size={11} />
            </a>
          </span>
        ))}
        {aRelancer > 0 && (
          <button
            data-testid="prospect-filter-relance"
            onClick={() => setFilterSport("relance")}
            title={`Contactés depuis 14 jours ou plus, sans réponse — à relancer`}
            className={`px-4 py-1.5 rounded-full border text-xs transition-colors ${filterSport === "relance" ? "bg-amber-500 text-ink border-amber-500 font-bold" : "border-amber-500/50 text-amber-400/90 hover:bg-amber-500/15"}`}
          >
            À relancer ({aRelancer})
          </button>
        )}
        <div className="flex-1" />
        <form
          onSubmit={(e) => { e.preventDefault(); setPage(1); clearSelection(); load(1); }}
          className="flex items-center gap-2"
        >
          <input
            data-testid="prospect-search-input"
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Club, courriel, ville, téléphone…"
            title="Recherche par ville, sport (boutons ci-dessus), club, courriel ou téléphone"
            className="bg-ink border border-ivory/15 rounded-full px-4 py-2 text-xs text-ivory placeholder:text-ivory/40 focus:border-gold/60 focus:outline-none w-56"
          />
          <button type="submit" data-testid="prospect-search-btn" className="p-2 border border-ivory/20 rounded-full text-ivory/60 hover:text-gold hover:border-gold/50 transition-colors">
            <Search size={14} />
          </button>
        </form>
        <button
          type="button"
          data-testid="prospect-select-all"
          onClick={() => toggleKind("all")}
          title={`Cocher TOUT le résultat affiché (${filtered} fiches, toutes pages confondues) — recliquer pour décocher`}
          className={`inline-flex items-center gap-2 border px-4 py-2 rounded-full text-xs transition-colors duration-300 ${
            selectKind === "all" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"
          }`}
        >
          <UserCheck size={14} /> Tout cocher ({filtered})
        </button>
        <button
          data-testid="prospect-campaign-btn"
          onClick={() => { setCampSport(filterSport !== "tous" ? filterSport : "Hockey"); setCampOpen(true); }}
          className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300"
        >
          <Megaphone size={14} /> Envoyer une campagne
        </button>
        <button
          type="button"
          data-testid="prospect-verify-btn"
          onClick={validerAdresses}
          disabled={verifBusy || (verifJob && !verifJob.done)}
          title="Teste la délivrabilité AVANT la campagne : syntaxe, serveur courriel du domaine (MX) et sondage de la boîte. Les adresses problématiques (point rouge) sont exclues d'office des campagnes."
          className="inline-flex items-center gap-2 border border-sky-400/40 text-sky-300 px-5 py-2 rounded-full text-xs hover:bg-sky-400/10 transition-colors duration-300 disabled:opacity-50"
        >
          {(verifBusy || (verifJob && !verifJob.done)) ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
          {verifJob && !verifJob.done
            ? `Validation en cours… ${verifJob.fait || 0}/${verifJob.total || 0}`
            : `Valider les adresses${nSelected > 0 ? ` (${nSelected})` : " (toutes)"}`}
        </button>
        <a
          href={exportUrl}
          data-testid="prospect-export-btn"
          className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300"
        >
          <Download size={14} /> Export CSV
        </a>
        <button
          type="button"
          data-testid="prospect-export-emails"
          onClick={() => toggleKind("emails")}
          title={`Cocher TOUTES les fiches avec courriel du résultat affiché (${withEmail}) — pour un futur envoi ; « Excel de la sélection » donne ensuite la liste complète des fiches cochées`}
          className={`inline-flex items-center gap-2 border px-4 py-2 rounded-full text-xs transition-colors duration-300 ${
            selectKind === "emails" ? "bg-emerald-500 text-ink border-emerald-500 font-bold" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-ink"
          }`}
        >
          <Mail size={14} /> Tous les courriels ({withEmail})
        </button>
        <button
          type="button"
          data-testid="prospect-export-phones"
          onClick={() => toggleKind("phones")}
          title={`Cocher TOUTES les fiches avec téléphone du résultat affiché (${withPhone}) — pour un futur envoi ; « Excel de la sélection » donne ensuite la liste complète des fiches cochées`}
          className={`inline-flex items-center gap-2 border px-4 py-2 rounded-full text-xs transition-colors duration-300 ${
            selectKind === "phones" ? "bg-emerald-500 text-ink border-emerald-500 font-bold" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500 hover:text-ink"
          }`}
        >
          <Phone size={14} /> Tous les téléphones ({withPhone})
        </button>
      </div>

      {/* Compte no-reply@trophee.ca : suivi des rebonds retirés + relevé manuel */}
      <div className="border border-ivory/15 rounded-xl px-5 py-4" data-testid="noreply-card">
        <div className="flex flex-wrap items-center gap-3">
          <Mail size={15} className="text-gold shrink-0" />
          <p className="text-xs text-ivory/75 flex-1 min-w-[240px]">
            <strong className="text-ivory">Boîte de prospection no-reply@trophee.ca</strong> — relevée
            automatiquement toutes les 5 minutes : les retours « mail delivery system » retirent
            l'adresse des envois, les réponses des clients sont acheminées à info@trophee.ca.
            {nrRebonds && (
              <span className="text-ivory/55" data-testid="noreply-rebonds-count">
                {" "}· <strong className={nrRebonds.items.length ? "text-red-300" : "text-emerald-400"}>{nrRebonds.items.length}</strong> adresse(s) retirée(s) par rebond
              </span>
            )}
          </p>
          {nrRebonds && !nrRebonds.configure && (
            <span className="text-xs text-amber-300/90" data-testid="noreply-not-configured">
              Mot de passe de la boîte non configuré — relevé inactif
            </span>
          )}
          <button data-testid="noreply-poll-btn" onClick={releverNoreply} disabled={nrBusy}
            className="inline-flex items-center gap-2 border border-ivory/25 text-ivory/70 px-4 py-1.5 rounded-full text-xs hover:border-gold hover:text-gold transition-colors disabled:opacity-50">
            {nrBusy ? <Loader2 size={13} className="animate-spin" /> : <Radar size={13} />}
            Relever maintenant
          </button>
          <button data-testid="noreply-test-btn" onClick={async () => {
              setNrBusy(true);
              try {
                const { data } = await api.post("/admin/prospects/test-campagne",
                  { emails: ["info@trophee.ca", "commande@trophee.ca"] });
                const ok = Object.values(data.resultats || {}).every((r) => r === "smtp");
                ok ? toast.success("Courriel test envoyé à info@ et commande@ (depuis no-reply@trophee.ca)")
                   : toast.warning("Envoi partiel — voir les journaux");
              } catch (err) {
                toast.error(formatApiError(err.response?.data?.detail));
              } finally { setNrBusy(false); }
            }} disabled={nrBusy}
            className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-1.5 rounded-full text-xs hover:bg-gold/10 transition-colors disabled:opacity-50">
            <Mail size={13} /> M'envoyer un test
          </button>
        </div>
        {nrRebonds && nrRebonds.items.length > 0 && (
          <p className="text-[11px] text-ivory/50 mt-2 truncate" data-testid="noreply-rebonds-last">
            Dernier retrait : {nrRebonds.items[0].email} — {fmtDateMtl(nrRebonds.items[0].at)}
          </p>
        )}
      </div>

      {/* Journal des envois de campagne (demande du propriétaire) : statut en
          direct de chaque mass mailing, avec pause / reprise / arrêt complet */}
      {campJobs.length > 0 && (
        <div className="border border-ivory/15 rounded-xl px-5 py-4" data-testid="campaign-jobs-journal">
          <p className="text-[11px] uppercase tracking-widest text-gold mb-3 flex items-center gap-2">
            <Megaphone size={13} /> Journal des envois
          </p>
          <div className="space-y-3">
            {campJobs.map((j) => {
              const fait = (j.sent || 0) + (j.queued || 0) + (j.skipped || 0);
              const pct = j.total ? Math.min(100, Math.round((fait / j.total) * 100)) : 0;
              return (
                <div key={j.id} data-testid={`campaign-job-${j.id}`}
                  className="border border-ivory/10 rounded-lg px-4 py-3">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    <p className="text-sm font-medium flex-1 min-w-[200px] truncate" title={j.subject}>
                      {j.subject}
                      <span className="text-ivory/50 text-xs ml-2">({j.sport} · {fmtDateMtl(j.started_at)})</span>
                    </p>
                    <span data-testid={`campaign-job-status-${j.id}`}
                      className={`text-xs px-3 py-1 rounded-full border ${
                        j.done
                          ? j.stopped ? "border-red-400/40 text-red-300" : "border-emerald-400/40 text-emerald-300"
                          : j.paused ? "border-amber-400/40 text-amber-300" : "border-gold/40 text-gold"}`}>
                      {j.done ? (j.stopped ? "Arrêtée" : "Terminée") : j.paused ? "En pause" : "Envoi en cours…"}
                    </span>
                    {!j.done && (
                      <span className="flex items-center gap-2">
                        {j.paused ? (
                          <button data-testid={`campaign-job-resume-${j.id}`} onClick={() => campJobControl(j.id, "resume")}
                            disabled={campJobBusy === `resume-${j.id}`}
                            className="inline-flex items-center gap-1.5 text-xs border border-emerald-400/50 text-emerald-300 px-3 py-1.5 rounded-full hover:bg-emerald-400 hover:text-ink transition-colors disabled:opacity-50">
                            {campJobBusy === `resume-${j.id}` ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} Reprendre
                          </button>
                        ) : (
                          <button data-testid={`campaign-job-pause-${j.id}`} onClick={() => campJobControl(j.id, "pause")}
                            disabled={campJobBusy === `pause-${j.id}`}
                            className="inline-flex items-center gap-1.5 text-xs border border-amber-400/50 text-amber-300 px-3 py-1.5 rounded-full hover:bg-amber-400 hover:text-ink transition-colors disabled:opacity-50">
                            {campJobBusy === `pause-${j.id}` ? <Loader2 size={13} className="animate-spin" /> : <Pause size={13} />} Pause
                          </button>
                        )}
                        <button data-testid={`campaign-job-stop-${j.id}`} onClick={() => campJobControl(j.id, "stop")}
                          disabled={campJobBusy === `stop-${j.id}`}
                          className="inline-flex items-center gap-1.5 text-xs border border-red-400/50 text-red-300 px-3 py-1.5 rounded-full hover:bg-red-400 hover:text-ink transition-colors disabled:opacity-50">
                          {campJobBusy === `stop-${j.id}` ? <Loader2 size={13} className="animate-spin" /> : <Square size={13} />} Arrêter
                        </button>
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-2 flex-1 rounded-full bg-ivory/10 overflow-hidden">
                      <div data-testid={`campaign-job-bar-${j.id}`}
                        className={`h-full transition-all duration-500 ${j.done ? (j.stopped ? "bg-red-400" : "bg-emerald-400") : j.paused ? "bg-amber-400" : "bg-gold"}`}
                        style={{ width: `${pct}%` }} />
                    </div>
                    <span className="text-[11px] text-ivory/60 whitespace-nowrap" data-testid={`campaign-job-counts-${j.id}`}>
                      {pct} % — {j.sent || 0} envoyé(s){j.queued ? `, ${j.queued} en file` : ""}{j.skipped ? `, ${j.skipped} sauté(s)` : ""} sur {j.total}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Barre d'actions de la sélection — visible dès qu'une case est cochée ou un mode global actif */}
      {(nSelected > 0 || selectKind) && (
        <div className="flex flex-wrap items-center gap-3 bg-red-950/30 border border-red-500/30 rounded-xl px-4 py-2.5" data-testid="prospects-bulk-bar">
          {selectKind === "all" ? (
            <span className="text-xs text-ivory/80" data-testid="prospects-bulk-all">
              Tous les <strong className="text-gold">{filtered}</strong> résultats sont cochés (toutes pages)
            </span>
          ) : selectKind === "emails" ? (
            <span className="text-xs text-ivory/80" data-testid="prospects-bulk-emails">
              Toutes les fiches <strong className="text-emerald-400">avec courriel</strong> du résultat : <strong className="text-emerald-400">{withEmail}</strong> {withEmail > 1 ? "cochées" : "cochée"} — {withEmail > 1 ? "prêtes" : "prête"} pour un envoi
            </span>
          ) : selectKind === "phones" ? (
            <span className="text-xs text-ivory/80" data-testid="prospects-bulk-phones">
              Toutes les fiches <strong className="text-emerald-400">avec téléphone</strong> du résultat : <strong className="text-emerald-400">{withPhone}</strong> {withPhone > 1 ? "cochées" : "cochée"}
            </span>
          ) : (
            <span className="text-xs text-ivory/80"><strong className="text-red-300">{nSelected}</strong> {plur(nSelected, "fiche")} {nSelected > 1 ? "cochées" : "cochée"}</span>
          )}
          <button data-testid="prospects-bulk-excel" onClick={exportSelection} disabled={exportBusy}
            className="inline-flex items-center gap-2 bg-emerald-600/90 text-white font-bold px-4 py-1.5 rounded-full text-xs hover:bg-emerald-600 transition-colors disabled:opacity-60">
            {exportBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            Excel de la sélection
          </button>
          <button data-testid="prospects-bulk-delete" onClick={removeSelected}
            className="inline-flex items-center gap-2 bg-red-500/90 text-white font-bold px-4 py-1.5 rounded-full text-xs hover:bg-red-500 transition-colors">
            <Trash2 size={13} /> Supprimer la sélection
          </button>
          <button data-testid="prospects-bulk-clear" onClick={clearSelection}
            className="text-xs text-ivory/60 hover:text-gold underline underline-offset-2">
            Vider la sélection
          </button>
        </div>
      )}

      <div className="bg-surface border border-ivory/10 rounded-xl overflow-hidden" data-testid="prospects-table">
        <Table>
          <TableHeader>
            <TableRow className="border-ivory/10 hover:bg-transparent">
              <TableHead className="w-8">
                <input type="checkbox" data-testid="prospect-check-page"
                  checked={prospects.length > 0 && prospects.every((p) => rowChecked(p))}
                  onChange={(e) => toggleSelectPage(e.target.checked)}
                  title="Cocher toute la page (bouton « Tout cocher » pour toutes les pages)"
                  className="accent-[#d4af37]" />
              </TableHead>
              <SortHead field="org" testid="prospect-sort-nom">Nom</SortHead>
              <SortHead field="phone" testid="prospect-sort-telephone">Téléphone</SortHead>
              <SortHead field="email" testid="prospect-sort-courriel">Courriel</SortHead>
              <SortHead field="city" testid="prospect-sort-ville">Ville</SortHead>
              <SortHead field="sport" testid="prospect-sort-sport">Sport</SortHead>
              <SortHead field="status" testid="prospect-sort-statut">Statut</SortHead>
              <SortHead field="score" testid="prospect-sort-score">Score</SortHead>
              <SortHead field="created_at" testid="prospect-sort-ajoute">Ajouté le</SortHead>
              <TableHead className="text-ivory/60 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow className="border-ivory/10 hover:bg-transparent">
                <TableCell colSpan={9} className="text-center py-10 text-ivory/50">
                  <Loader2 size={18} className="animate-spin inline-block" />
                </TableCell>
              </TableRow>
            ) : prospects.length === 0 ? (
              <TableRow className="border-ivory/10 hover:bg-transparent">
                <TableCell colSpan={9} className="text-center py-10 text-ivory/50" data-testid="prospects-empty">
                  Aucun prospect pour le moment — analysez le site d'un organisme ci-dessus.
                </TableCell>
              </TableRow>
            ) : prospects.map((p) => {
              const k = p.email || p.id; // clé testid unique même pour les contacts sans courriel
              return (
              <TableRow key={p.id} className={`border-ivory/10 hover:bg-ivory/[0.03] ${rowChecked(p) ? "bg-gold/[0.06]" : ""}`} data-testid={`prospect-row-${k}`}>
                <TableCell className="w-8">
                  <input type="checkbox" data-testid={`prospect-check-${k}`}
                    checked={rowChecked(p)} onChange={(e) => toggleSelect(p.id, e.target.checked)}
                    title="Cocher pour l'export Excel ou la suppression"
                    className="accent-[#d4af37]" />
                </TableCell>
                <TableCell className="text-ivory/90 text-sm font-medium">
                  <a href={p.source_url || undefined} target="_blank" rel="noreferrer" className="hover:text-gold transition-colors underline-offset-2 hover:underline">
                    {p.org}
                  </a>
                  {p.manual_edit && (
                    <span className="inline-block text-[9px] text-gold/80 border border-gold/30 rounded-full px-1.5 py-0.5 ml-1.5 align-middle"
                      title="Modifié manuellement — protégé : aucune capture ni recherche ne peut écraser cette fiche"
                      data-testid={`prospect-manual-${k}`}>manuel</span>
                  )}
                </TableCell>
                <TableCell className="text-sm">
                  {p.phone ? (
                    <a href={`tel:${p.phone.replace(/[^0-9+]/g, "")}`} data-testid={`prospect-phone-${k}`}
                      className="inline-flex items-center gap-1.5 text-amber-300 hover:text-gold transition-colors whitespace-nowrap">
                      <Phone size={13} /> {p.phone}
                    </a>
                  ) : <span className="text-ivory/40">—</span>}
                </TableCell>
                <TableCell className="text-ivory text-sm">
                  {p.email ? (
                    <span className="break-all">{p.email}
                      {p.verif && (
                        <span data-testid={`prospect-verif-${k}`}
                          title={`${VERIF_LABELS[p.verif] || p.verif}${p.verif_detail ? " — " + p.verif_detail : ""}${p.verif_at ? " (" + fmtDateMtl(p.verif_at) + ")" : ""}`}
                          className={`inline-block w-2 h-2 rounded-full ml-1.5 align-middle ${VERIF_COLORS[p.verif] || "bg-ivory/30"}`} />
                      )}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <span className="text-ivory/40">—</span>
                      {p.social_url && (
                        <a href={messengerLink(p.social_url)} target="_blank" rel="noreferrer" data-testid={`prospect-messenger-${k}`}
                          className="inline-flex items-center gap-1 text-xs text-ivory/60 hover:text-gold transition-colors">
                          <MessageCircle size={12} /> Messenger
                        </a>
                      )}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-ivory/70 text-sm" data-testid={`prospect-city-${k}`}>{p.city || "—"}</TableCell>
                <TableCell>
                  <Select value={p.sport} onValueChange={(v) => update(p, "sport", v)}>
                    <SelectTrigger data-testid={`prospect-sport-${k}`} className="h-8 w-36 bg-ink border-ivory/15 rounded-full text-xs text-ivory">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-ivory/15 text-ivory">
                      {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <Select value={p.status} onValueChange={(v) => update(p, "status", v)}>
                    <SelectTrigger data-testid={`prospect-status-${k}`} className="h-8 w-32 bg-ink border-ivory/15 rounded-full text-xs text-ivory">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-surface border-ivory/15 text-ivory">
                      {Object.entries(STATUS_LABELS).map(([k2, l]) => <SelectItem key={k2} value={k2}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {p.unsubscribed && (
                    <span className="block text-[10px] text-red-400 mt-1 pl-1" data-testid={`prospect-unsub-${k}`}>
                      désabonné — ne plus écrire
                    </span>
                  )}
                  {p.contacted_at && (
                    <span className="block text-[10px] text-ivory/45 mt-1 pl-1" data-testid={`prospect-contacted-${k}`}>
                      contacté le {new Date(p.contacted_at).toLocaleString("fr-CA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  )}
                  <span className="flex items-center gap-1.5 mt-1 pl-1">
                    <span className="text-[10px] text-ivory/45 shrink-0">relance :</span>
                    <input
                      type="date"
                      value={p.relance_le || ""}
                      onChange={(e) => update(p, "relance_le", e.target.value)}
                      data-testid={`prospect-relance-${k}`}
                      title="Date de la prochaine relance prévue (rappel dans l'aperçu)"
                      className={`bg-ink border rounded px-1.5 py-0.5 text-[10px] [color-scheme:dark] transition-colors ${
                        p.relance_le && p.relance_le <= new Date().toISOString().slice(0, 10) && ["nouveau", "contacte"].includes(p.status)
                          ? "border-amber-500/60 text-amber-400" : "border-ivory/15 text-ivory/70"
                      }`}
                    />
                  </span>
                </TableCell>
                <TableCell data-testid={`prospect-score-${k}`}>
                  <span className="inline-flex items-center gap-1.5" title="Score d'engagement : répondu +100 · clic +30 · ouverture +10 — les plus engagés remontent en tête">
                    <span className={`font-mono text-sm font-bold ${
                      (p.score || 0) >= 100 ? "text-gold" : (p.score || 0) > 0 ? "text-ivory/85" : "text-ivory/35"
                    }`}>{p.score || 0}</span>
                    {p.repondu && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 whitespace-nowrap"
                        title={p.repondu_at ? `A répondu le ${fmtDateMtl(p.repondu_at)}` : "A répondu"}>
                        Répondu
                      </span>
                    )}
                    {!p.repondu && (p.clics || 0) > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-sky-500/15 text-sky-300 border border-sky-500/30 whitespace-nowrap"
                        title={p.clique_at ? `A cliqué le ${fmtDateMtl(p.clique_at)}` : "A cliqué"}>
                        A cliqué
                      </span>
                    )}
                    {!p.repondu && !(p.clics || 0) && (p.ouvertures || 0) > 0 && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-ivory/10 text-ivory/60 border border-ivory/20 whitespace-nowrap"
                        title={p.ouvert_at ? `A ouvert le ${fmtDateMtl(p.ouvert_at)}` : "A ouvert"}>
                        A ouvert
                      </span>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-ivory/50 text-xs">{fmtDateMtl(p.created_at)}</TableCell>
                <TableCell className="text-right">
                  <span className="inline-flex items-center gap-0.5">
                    <button
                      data-testid={`prospect-view-${k}`}
                      onClick={() => setViewProspect(p)}
                      className="p-2 text-ivory/40 hover:text-gold transition-colors"
                      title="Voir la fiche complète"
                    >
                      <Eye size={15} />
                    </button>
                    <button
                      data-testid={`prospect-edit-${k}`}
                      onClick={() => setEditProspect({ ...p })}
                      className="p-2 text-ivory/40 hover:text-gold transition-colors"
                      title="Modifier la fiche au complet (protégée ensuite contre tout écrasement automatique)"
                    >
                      <Pencil size={15} />
                    </button>
                    {p.email && (
                      <button
                        data-testid={`prospect-write-${k}`}
                        onClick={() => openWrite(p, "edit")}
                        className="p-2 text-ivory/40 hover:text-gold transition-colors"
                        title="Écrire un courriel à ce prospect"
                      >
                        <Mail size={15} />
                      </button>
                    )}
                    <button
                      data-testid={`prospect-delete-${k}`}
                      onClick={() => remove(p)}
                      className="p-2 text-ivory/40 hover:text-red-400 transition-colors"
                      title="Supprimer"
                    >
                      <Trash2 size={15} />
                    </button>
                  </span>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>

        {/* Pagination : 25/50/100 par page — la liste reste légère */}
        <div className="flex flex-wrap items-center justify-between gap-3 mt-4" data-testid="prospects-pager">
          <span className="inline-flex items-center gap-2 text-xs text-ivory/60">
            Par page :
            {[25, 50, 100].map((n) => (
              <button key={n} data-testid={`pager-size-${n}`} onClick={() => setPageSize(n)}
                className={`px-3 py-1 rounded-full border text-xs transition-colors ${pageSize === n ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
                {n}
              </button>
            ))}
          </span>
          <span className="text-xs text-ivory/60" data-testid="pager-status">
            <strong className="text-gold">{filtered}</strong> prospect(s) — page {page} / {pages}
          </span>
          <span className="inline-flex items-center gap-2">
            <button data-testid="pager-prev" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
              className="px-4 py-1.5 rounded-full border border-ivory/20 text-xs text-ivory/70 hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
              ← Précédent
            </button>
            <button data-testid="pager-next" onClick={() => setPage((p) => Math.min(pages, p + 1))} disabled={page >= pages}
              className="px-4 py-1.5 rounded-full border border-ivory/20 text-xs text-ivory/70 hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
              Suivant →
            </button>
          </span>
        </div>
      </div>

      {/* Fiche complète d'un prospect — lecture */}
      <Dialog open={!!viewProspect} onOpenChange={(o) => !o && setViewProspect(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg" data-testid="prospect-view-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <Eye className="w-5 h-5 text-gold" /> {viewProspect?.org || "Fiche prospect"}
            </DialogTitle>
            <DialogDescription className="sr-only">Fiche complète du prospect</DialogDescription>
          </DialogHeader>
          {viewProspect && (
            <div className="space-y-2.5 text-sm">
              {viewProspect.manual_edit && (
                <p className="text-xs text-gold border border-gold/30 rounded-lg px-3 py-2" data-testid="view-manual-flag">
                  Fiche modifiée manuellement — protégée : aucune capture ni recherche ne peut l'écraser.
                </p>
              )}
              {[
                ["Courriel", viewProspect.email || "— (aucun)"],
                ["Organisme", viewProspect.org || "—"],
                ["Téléphone", viewProspect.phone || "—"],
                ["Adresse", viewProspect.address || "—"],
                ["Ville", viewProspect.city || "—"],
                ["Catégorie", viewProspect.sport || "—"],
                ["Statut", STATUS_LABELS[viewProspect.status] || viewProspect.status || "—"],
                ["Site / source", viewProspect.source_url || "—"],
                ["Page sociale", viewProspect.social_url || "—"],
                ["Ajouté le", (viewProspect.created_at || "").slice(0, 10) || "—"],
                ["Contacté le", viewProspect.contacted_at ? new Date(viewProspect.contacted_at).toLocaleString("fr-CA") : "—"],
                ["Relance prévue", viewProspect.relance_le || "—"],
                ["Origine", viewProspect.source === "recolte-60km" ? "Récolte 60 km (automatique)" : (viewProspect.source || "capture")],
              ].map(([label, val]) => (
                <div key={label} className="flex gap-3 border-b border-ivory/8 pb-2">
                  <span className="w-32 shrink-0 text-ivory/50 text-xs pt-0.5">{label}</span>
                  <span className="text-ivory/90 break-all">{val}</span>
                </div>
              ))}
              <div className="flex flex-wrap gap-2 pt-3">
                <button data-testid="view-goto-edit" onClick={() => { setEditProspect({ ...viewProspect }); setViewProspect(null); }}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all">
                  <Pencil size={13} /> Modifier la fiche
                </button>
                {viewProspect.email && (
                  <button data-testid="view-goto-write" onClick={() => { setViewProspect(null); openWrite(viewProspect, "edit"); }}
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors">
                    <Mail size={13} /> Écrire un courriel
                  </button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Fiche complète d'un prospect — modification (pose le verrou anti-écrasement) */}
      <Dialog open={!!editProspect} onOpenChange={(o) => !o && setEditProspect(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-lg max-h-[90vh] overflow-y-auto" data-testid="prospect-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <Pencil className="w-5 h-5 text-gold" /> Modifier la fiche
            </DialogTitle>
            <DialogDescription className="text-ivory/60 text-xs">
              Une fiche modifiée à la main est ensuite <strong className="text-gold">protégée</strong> : aucune capture ni recherche ne peut l'écraser.
            </DialogDescription>
          </DialogHeader>
          {editProspect && (
            <div className="space-y-3">
              {[
                ["Organisme", "org", "text"],
                ["Courriel", "email", "text"],
                ["Téléphone", "phone", "text"],
                ["Adresse", "address", "text"],
                ["Ville", "city", "text"],
                ["Site / source", "source_url", "text"],
              ].map(([label, field]) => (
                <div key={field}>
                  <label className="block text-xs text-ivory/60 mb-1">{label}</label>
                  <input data-testid={`edit-${field}`} value={editProspect[field] || ""}
                    onChange={(e) => setEditProspect((s) => ({ ...s, [field]: e.target.value }))}
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-3.5 py-2.5 text-sm focus:border-gold focus:outline-none" />
                </div>
              ))}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-ivory/60 mb-1">Catégorie</label>
                  <Select value={editProspect.sport} onValueChange={(v) => setEditProspect((s) => ({ ...s, sport: v }))}>
                    <SelectTrigger data-testid="edit-sport" className="bg-ink border-ivory/15 rounded-lg text-sm text-ivory"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-ivory/15 text-ivory">
                      {SPORTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-ivory/60 mb-1">Statut</label>
                  <Select value={editProspect.status} onValueChange={(v) => setEditProspect((s) => ({ ...s, status: v }))}>
                    <SelectTrigger data-testid="edit-status" className="bg-ink border-ivory/15 rounded-lg text-sm text-ivory"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-surface border-ivory/15 text-ivory">
                      {Object.entries(STATUS_LABELS).map(([k2, l]) => <SelectItem key={k2} value={k2}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="block text-xs text-ivory/60 mb-1">Relance prévue le</label>
                <input type="date" data-testid="edit-relance" value={editProspect.relance_le || ""}
                  onChange={(e) => setEditProspect((s) => ({ ...s, relance_le: e.target.value }))}
                  className="bg-ink border border-ivory/15 rounded-lg px-3.5 py-2.5 text-sm [color-scheme:dark] focus:border-gold focus:outline-none" />
              </div>
              <div className="flex gap-2 pt-2">
                <button data-testid="edit-save" onClick={saveEdit} disabled={editBusy}
                  className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-6 py-2.5 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all disabled:opacity-50">
                  {editBusy ? <Loader2 size={13} className="animate-spin" /> : <Pencil size={13} />} Enregistrer la fiche
                </button>
                <button onClick={() => setEditProspect(null)}
                  className="text-xs text-ivory/60 hover:text-gold transition-colors px-3">
                  Annuler
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Générateur de campagne B2B par sport */}
      <Dialog open={campOpen} onOpenChange={setCampOpen}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-campaign-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-3">
              <Megaphone className="w-5 h-5 text-gold" /> Campagne par sport
            </DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              Le message est pré-écrit selon le sport. Règles en vigueur : l'envoi part de
              no-reply@trophee.ca (maximum 100 courriels/heure), le courriel est bilingue
              FR + EN avec la signature (logo or, Messenger), les réponses des prospects
              arrivent à info@trophee.ca, et les adresses en rebond sont retirées
              automatiquement des envois.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-2">
            <div>
              <div className="flex items-center justify-between mb-1.5 gap-3">
                <label className="block text-sm text-ivory/70">Sport ciblé</label>
                <button onClick={aiDraft} disabled={aiBusy || draftBusy}
                  data-testid="campaign-ai-btn"
                  className="inline-flex items-center gap-2 border border-gold/50 text-gold px-4 py-1.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300 disabled:opacity-50">
                  {aiBusy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
                  {aiBusy ? "Rédaction en cours…" : "Rédiger par l'IA"}
                </button>
              </div>
              <Select value={campSport} onValueChange={setCampSport}>
                <SelectTrigger data-testid="campaign-sport-select" className="bg-ink border-ivory/15 text-ivory">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-surface border-ivory/15 text-ivory">
                  {SPORTS.filter((s) => s !== "Autre").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {draftBusy ? (
              <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
            ) : draft && (
              <>
                <p className="text-xs text-ivory/60" data-testid="campaign-count">
                  <strong className="text-gold">{draft.count}</strong> destinataire{draft.count > 1 ? "s" : ""} (statut ≠ inactif)
                </p>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Objet</label>
                  <input value={campSubject} onChange={(e) => setCampSubject(e.target.value)}
                    data-testid="campaign-subject"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
                </div>
                <div>
                  <label className="block text-sm text-ivory/70 mb-1.5">Message</label>
                  <textarea rows={12} value={campBody} onChange={(e) => setCampBody(e.target.value)}
                    data-testid="campaign-body"
                    className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none leading-relaxed" />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <button onClick={saveTemplate} data-testid="campaign-save-template"
                    title="Votre version modifiée devient le gabarit utilisé automatiquement pour ce sport"
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-4 py-2 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300">
                    Sauvegarder comme gabarit « {campSport} »
                  </button>
                  {draft.custom && (
                    <>
                      <span className="text-[10px] uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1"
                        data-testid="campaign-custom-badge">Gabarit personnalisé</span>
                      <button onClick={resetTemplate} data-testid="campaign-reset-template"
                        className="text-xs text-ivory/50 hover:text-gold underline underline-offset-2 transition-colors">
                        Revenir au modèle par défaut
                      </button>
                    </>
                  )}
                </div>
                <div className="max-h-28 overflow-y-auto flex flex-wrap gap-1.5" data-testid="campaign-recipients">
                  {draft.emails.map((e) => (
                    <span key={e} className="text-[10px] bg-ink border border-ivory/15 text-ivory/70 rounded-full px-2.5 py-0.5">{e}</span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-3 pt-2">
                  <button onClick={showApercu} disabled={apercuBusy || !campBody.trim()}
                    data-testid="campaign-preview"
                    title="Montre le courriel TEL QUEL le prospect le recevra : gabarit bilingue, bandeau « English will follow », signature avec logo or et bouton Messenger"
                    className="inline-flex items-center gap-2 border border-sky-400/40 text-sky-300 px-5 py-2.5 rounded-full text-xs hover:bg-sky-400/10 transition-colors duration-300 disabled:opacity-50">
                    {apercuBusy ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
                    Aperçu du courriel réel
                  </button>
                  <button onClick={sendCampaign} disabled={sendBusy || draft.count === 0}
                    data-testid="campaign-send"
                    title="Envoie le courriel à tout le groupe sélectionné — maximum 100 courriels/heure, le surplus part automatiquement à l'heure suivante"
                    className="inline-flex items-center gap-2 bg-emerald-500 text-ink font-bold px-5 py-2.5 rounded-full text-xs hover:bg-emerald-400 transition-all duration-300 disabled:opacity-50">
                    {sendBusy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Envoyer au groupe ({draft.count})
                  </button>
                  <button onClick={() => copyText(draft.emails.join(", "), `${draft.count} adresse(s) copiée(s)`)}
                    data-testid="campaign-copy-emails"
                    className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2.5 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300">
                    <Copy size={14} /> Copier les adresses
                  </button>
                  <button onClick={() => copyText(`${campSubject}\n\n${campBody}`, "Message copié")}
                    data-testid="campaign-copy-message"
                    className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300">
                    <Copy size={14} /> Copier le message
                  </button>
                  {draft.count > 0 && (
                    <a href={`mailto:?bcc=${encodeURIComponent(draft.emails.join(","))}&subject=${encodeURIComponent(campSubject)}`}
                      data-testid="campaign-mailto"
                      className="inline-flex items-center gap-2 border border-ivory/25 text-ivory px-5 py-2.5 rounded-full text-xs hover:border-gold hover:text-gold transition-colors duration-300">
                      <Mail size={14} /> Ouvrir dans mon courriel (CCI)
                    </a>
                  )}
                  {/* Marquage « Contacté » : MANUEL — à cliquer seulement après l'envoi réel */}
                  <button onClick={markContacted}
                    data-testid="campaign-mark-contacted"
                    title="À utiliser une fois le courriel réellement envoyé"
                    className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-400 px-5 py-2.5 rounded-full text-xs hover:bg-emerald-500 hover:text-ink transition-colors duration-300">
                    <UserCheck size={14} /> Marquer comme contactés
                  </button>
                </div>
                <p className="text-[11px] text-ivory/50">
                  Conseil : collez les adresses en CCI (copie carbone invisible) dans votre courriel
                  info@trophee.ca pour que chaque destinataire ne voie pas les autres.
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Écrire à UN prospect : aperçu (œil) ou modification (crayon) avant l'envoi */}
      <Dialog open={!!writeProspect} onOpenChange={(o) => !o && setWriteProspect(null)}>
        <DialogContent className="bg-surface border-gold/20 text-ivory max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="prospect-write-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {writeMode === "preview" ? "Aperçu du message" : "Modifier le message"} — {writeProspect?.email}
            </DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              {writeProspect?.org} · {writeProspect?.sport} — l'envoi se fait depuis votre courriel (info@trophee.ca).
            </DialogDescription>
          </DialogHeader>

          <div className="flex gap-2 mb-4">
            <button onClick={() => setWriteMode("preview")} data-testid="write-mode-preview"
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs transition-colors ${writeMode === "preview" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
              <Eye size={13} /> Aperçu
            </button>
            <button onClick={() => setWriteMode("edit")} data-testid="write-mode-edit"
              className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs transition-colors ${writeMode === "edit" ? "bg-gold text-ink border-gold font-bold" : "border-ivory/20 text-ivory/60 hover:border-gold/50 hover:text-gold"}`}>
              <Pencil size={13} /> Modifier
            </button>
          </div>

          {writeBusy ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-gold" /></div>
          ) : writeMode === "preview" ? (
            /* Aperçu tel qu'écrit : rendu courriel simple sur fond clair */
            <div className="bg-white text-gray-900 rounded-xl p-6 shadow-inner" data-testid="write-preview-pane">
              <p className="text-[11px] uppercase tracking-widest text-gray-400 mb-1">Objet</p>
              <p className="font-semibold text-base mb-4 pb-3 border-b border-gray-200" data-testid="write-preview-subject">{writeSubject}</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" data-testid="write-preview-body">{writeBody}</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Objet</label>
                <input value={writeSubject} onChange={(e) => setWriteSubject(e.target.value)}
                  data-testid="write-subject"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none" />
              </div>
              <div>
                <label className="block text-sm text-ivory/70 mb-1.5">Message</label>
                <textarea rows={12} value={writeBody} onChange={(e) => setWriteBody(e.target.value)}
                  data-testid="write-body"
                  className="w-full bg-ink border border-ivory/15 rounded-lg px-4 py-3 text-sm focus:border-gold focus:outline-none leading-relaxed" />
              </div>
            </div>
          )}

          {writeProspect && !writeBusy && (
            <div className="flex flex-wrap gap-3 pt-4 border-t border-ivory/10 mt-4">
              <button onClick={() => copyText(`${writeSubject}\n\n${writeBody}`, "Message copié")}
                data-testid="write-copy"
                className="inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2.5 rounded-full text-xs hover:shadow-[0_0_18px_rgba(212,175,55,0.35)] transition-all duration-300">
                <Copy size={14} /> Copier le message
              </button>
              <a href={`mailto:${writeProspect.email}?subject=${encodeURIComponent(writeSubject)}&body=${encodeURIComponent(writeBody)}`}
                data-testid="write-mailto"
                className="inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-2.5 rounded-full text-xs hover:bg-gold hover:text-ink transition-colors duration-300">
                <Mail size={14} /> Ouvrir dans mon courriel
              </a>
              {/* Marquage « Contacté » : MANUEL — à cliquer après l'envoi réel */}
              {writeProspect.status === "nouveau" && (
                <button onClick={() => { markOneContacted(writeProspect); setWriteProspect(null); }}
                  data-testid="write-mark-contacted"
                  title="À utiliser une fois le courriel réellement envoyé"
                  className="inline-flex items-center gap-2 border border-emerald-500/40 text-emerald-400 px-5 py-2.5 rounded-full text-xs hover:bg-emerald-500 hover:text-ink transition-colors duration-300">
                  <UserCheck size={14} /> Marquer « Contacté »
                </button>
              )}
              <button onClick={() => setWriteProspect(null)} data-testid="write-close"
                className="inline-flex items-center gap-2 border border-ivory/25 text-ivory/70 px-5 py-2.5 rounded-full text-xs hover:border-ivory/60 transition-colors duration-300">
                <X size={14} /> Fermer
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Aperçu du courriel de prospection TEL QUEL reçu (rendu réel serveur) */}
      <Dialog open={!!apercu} onOpenChange={(o) => { if (!o) setApercu(null); }}>
        <DialogContent className="bg-surface border-ivory/15 text-ivory max-w-3xl" data-testid="campaign-preview-modal">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Aperçu du courriel réel</DialogTitle>
            <DialogDescription className="text-ivory/60 text-sm">
              Exactement ce que le prospect recevra : départ de no-reply@trophee.ca, gabarit
              bilingue, signature avec logo or et bouton Messenger.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white rounded-lg overflow-hidden">
            <iframe title="Aperçu du courriel" data-testid="campaign-preview-frame"
              srcDoc={apercu || ""} className="w-full h-[65vh] border-0" />
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={() => setApercu(null)} data-testid="campaign-preview-close"
              className="inline-flex items-center gap-2 border border-ivory/25 text-ivory/70 px-5 py-2 rounded-full text-xs hover:border-ivory/60 transition-colors">
              <X size={14} /> Fermer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
